import React from 'react';

import { Message } from '../../conversations/types/Conversation';
import { parseMessageContentWithEmbeddedMedia } from '../../conversations/utils/embeddedMediaFromText';

import styles from './EmbeddedWebWidget.module.css';

interface WebWidgetMediaMessageProps {
  message: Message;
}

export const WebWidgetMediaMessage: React.FC<WebWidgetMediaMessageProps> = ({ message }) => {
  if (message.messageType === 'image' && message.mediaUrl) {
    return (
      <div className={styles.mediaBlock}>
        <img
          className={styles.mediaImage}
          src={message.mediaUrl}
          alt={message.fileName || message.content || 'Image'}
          loading="lazy"
        />
        {message.content ? <div className={styles.mediaCaption}>{message.content}</div> : null}
      </div>
    );
  }

  if (message.messageType === 'video' && message.mediaUrl) {
    return (
      <div className={styles.mediaBlock}>
        <video className={styles.mediaVideo} src={message.mediaUrl} controls preload="metadata">
          <track kind="captions" />
        </video>
        {message.content ? <div className={styles.mediaCaption}>{message.content}</div> : null}
      </div>
    );
  }

  if (message.messageType === 'audio' && message.mediaUrl) {
    return (
      <div className={styles.mediaBlock}>
        <audio className={styles.mediaAudio} src={message.mediaUrl} controls preload="metadata" />
        {message.content ? <div className={styles.mediaCaption}>{message.content}</div> : null}
      </div>
    );
  }

  if ((message.messageType === 'document' || message.messageType === 'sticker') && message.mediaUrl) {
    return (
      <div className={styles.mediaBlock}>
        <a className={styles.mediaLink} href={message.mediaUrl} target="_blank" rel="noreferrer">
          {message.fileName || message.content || 'Download file'}
        </a>
        {message.content && message.fileName && message.content !== message.fileName
          ? <div className={styles.mediaCaption}>{message.content}</div>
          : null}
      </div>
    );
  }

  const contentSegments = parseMessageContentWithEmbeddedMedia(message.content || '');
  const hasInlineMedia = contentSegments.some(segment => segment.type === 'image' || segment.type === 'video');
  if (!hasInlineMedia) {
    return message.content ? <div className={styles.content}>{message.content}</div> : null;
  }

  return (
    <div className={styles.mediaBlock}>
      {contentSegments.map((segment, index) => {
        if (segment.type === 'text') {
          return <div key={`text-${index}`} className={styles.content}>{segment.value}</div>;
        }

        if (segment.type === 'video') {
          return (
            <video key={`video-${index}`} className={styles.mediaVideo} src={segment.url} controls preload="metadata">
              <track kind="captions" />
            </video>
          );
        }

        return (
          <img
            key={`image-${index}`}
            className={styles.mediaImage}
            src={segment.url}
            alt={segment.alt || 'Image'}
            loading="lazy"
          />
        );
      })}
    </div>
  );
};
